import React, { useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaBriefcase, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import { getCollageImages } from "../../utils/heroImages";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function Careers() {
  const positions = [
    {
      title: "Program Coordinator",
      location: "Houston, TX / Remote",
      type: "Full-time",
      description: "We're looking for an experienced program coordinator to help manage our scholarship and community development programs."
    },
    {
      title: "Community Outreach Manager",
      location: "Toronto, ON / Remote",
      type: "Full-time",
      description: "Join our team to help expand our community outreach efforts and build relationships with members and partners."
    },
    {
      title: "Marketing & Communications Specialist",
      location: "Remote",
      type: "Part-time",
      description: "Help us tell our story and engage our community through effective marketing and communications strategies."
    }
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Careers at Ojoto Union"
        subtitle="Join our team and help us make a difference in our community. Explore career opportunities with Ojoto Union North America."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        collageImages={getCollageImages("careers")}
      />

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Join Our Team
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We're always looking for passionate individuals who share our mission and want to contribute 
                to our community's growth and development.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Open Positions */}
      <section id="positions" className="section bg-gray-50">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Open Positions
              </h2>
            </div>
          </AnimatedSection>

          <div className="space-y-6 max-w-4xl mx-auto">
            {positions.map((position, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div className="card">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {position.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-2">
                          <FaMapMarkerAlt />
                          <span>{position.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaClock />
                          <span>{position.type}</span>
                        </div>
                      </div>
                      <p className="text-gray-600">
                        {position.description}
                      </p>
                    </div>
                    <Link to="/contact" className="btn-primary flex items-center gap-2">
                      Apply Now
                      <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Don't See a Position?
            </h2>
            <p 
              className="text-lg mb-8 max-w-2xl mx-auto"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              We're always interested in connecting with talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
